import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import {
  DashboardGoalSummaryListResponse,
  DashboardGoalSummaryRow,
  PtBookingDashboardListResponse,
  PtBookingDashboardRow,
  SwimmingCoachOverviewCounts,
  TrainingPlanSummaryFactListResponse,
  TrainingPlanSummaryFactRow,
  TrainerPerformanceOverviewCounts,
} from './model'

type GetPtBookingDashboardRowsParams = {
  from: string
  to: string
}

type GetPtDistinctTrainerCountParams = {
  trainerType?: 'PERMANENT' | 'FREELANCE'
  from?: string
  to?: string
}

type GetTrainerPerformanceOverviewCountsParams = {
  to?: string
}

type TrainerContractType = 'PERMANENT' | 'FREELANCE'

type TrainerListCountRow = {
  id?: number
  contractType?: TrainerContractType | null
}

type GetSwimmingCoachOverviewCountsParams = {
  to?: string
}

type GetSwimmingGoalSummaryRowsParams = {
  from: string
  to: string
  memberType: 'ADULT' | 'KID'
}

type GetTrainingPlanSummaryRowsParams = {
  year: number
  month: number
}

const PT_BOOKING_DASHBOARD_SELECT = [
  'pt_booking_id',
  'member_id',
  'member_code',
  'member_full_name',
  'trainer_id',
  'trainer_full_name',
  'trainer_type',
  'is_personal_training',
  'status',
  'start_time',
  'start_date_local',
  'start_month_local',
  'weekday',
  'weekday_index',
  'period',
  'duration_hours',
  'cancelled_sessions',
  'non_cancelled_sessions',
  'completed_sessions',
].join(',')

const POSTGREST_PAGE_SIZE = 500

const DASHBOARD_GOAL_SUMMARY_SELECT = [
  'goal_unit_key',
  'training_type',
  'goal_setting_id',
  'goal_type_id',
  'goal_type',
  'member_id',
  'member_code',
  'member_full_name',
  'trainer_id',
  'start_date',
  'end_date',
  'raw_status',
  'assessed_at',
  'status_updated_at',
  'computed_status',
  'assessment_status',
  'all_goals',
  'achieved_goals',
  'not_achieved_goals',
  'in_training_goals',
  'total_goals_to_assess',
  'on_time_assessments',
  'late_assessments',
  'unassessed_goals',
].join(',')

const TRAINING_PLAN_SUMMARY_FACT_SELECT = [
  'goal_session_group_id',
  'goal_setting_id',
  'training_plan_log_id',
  'actual_result_log_id',
  'member_id',
  'member_code',
  'member_full_name',
  'trainer_id',
  'trainer_full_name',
  'training_plan_created_at',
  'training_plan_session_date',
  'status_bucket',
  'planned_count',
  'await_result_count',
  'not_achieved_count',
  'achieved_count',
  'over_achieved_count',
  'execution_count',
  'success_count',
  'year_local',
  'month_local',
].join(',')

export const TrainerSummaryService = (client: AxiosInstance) => {
  const toNumber = (value: string | number | null | undefined) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const getTrainerListRows = async (): Promise<TrainerListCountRow[]> => {
    const { data: categories } = await wrapApiCall(() =>
      client.get<Record<string, number[]>>('/roles/categories')
    )
    const trainerRoleIds = (categories?.trainer ?? []).join(',')

    const fetchPage = (page: number) =>
      wrapApiCall(() =>
        client.get<{
          data?: TrainerListCountRow[]
          pagination?: { total?: number; pageSize?: number }
        }>('/users/trainers', {
          params: {
            page,
            pageSize: POSTGREST_PAGE_SIZE,
            roleIds: trainerRoleIds,
          },
        })
      )

    // `/users/trainers` always returns `pagination.total`, so page 1 tells us
    // the total up front and the remaining pages can be fetched in parallel
    // instead of one-at-a-time.
    const { data: firstData } = await fetchPage(1)
    const firstRows = firstData?.data ?? []
    const pageSize = Number(firstData?.pagination?.pageSize ?? POSTGREST_PAGE_SIZE)
    const total = Number(firstData?.pagination?.total ?? firstRows.length)
    const totalPages = clampTotalPages(total, pageSize, 'trainer-summary.getTrainerListRows')

    if (totalPages <= 1) {
      return firstRows
    }

    const restPages = await fetchPagesInBatches(
      Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
      (page) => fetchPage(page).then(({ data }) => data?.data ?? [])
    )

    return [firstRows, ...restPages].flat()
  }

  const getPrivateTrainerCount = async ({
    trainerIds,
    to,
  }: {
    trainerIds: Set<number>
    to?: string
  }) => {
    if (trainerIds.size === 0) {
      return 0
    }

    const countInRows = (
      pageRows: {
        trainer_id?: string | number | null
        total_private_trainers?: string | number | null
      }[]
    ) =>
      pageRows.reduce((acc, row) => {
        const trainerId = toNumber(row.trainer_id)
        return trainerIds.has(trainerId) && toNumber(row.total_private_trainers) > 0 ? acc + 1 : acc
      }, 0)

    const fetchPage = (page: number, withCount: boolean) => {
      const query = new URLSearchParams()
      query.append('select', 'trainer_id,total_private_trainers')
      query.append('page', String(page))
      query.append('pageSize', String(POSTGREST_PAGE_SIZE))
      if (withCount) {
        query.append('count', 'true')
      }

      if (to) {
        query.append('filter', `trainer_created_at=lt.${to}`)
      }

      return wrapApiCall(() =>
        client.get<{
          data?: {
            trainer_id?: string | number | null
            total_private_trainers?: string | number | null
          }[]
          pagination?: { total?: number }
        }>(`/postgrest/dashboard_trainer_profile_overview_view?${query.toString()}`)
      )
    }

    // Page 1 requests the count so we know the total up front; the
    // remaining pages skip `count=true` (the backend would otherwise re-run
    // the full view just to recompute an unchanging total) and are fetched
    // in parallel instead of sequentially, since the total is already known.
    const { data: firstData } = await fetchPage(1, true)
    const firstRows = firstData?.data ?? []
    const total = Number(firstData?.pagination?.total ?? firstRows.length)
    const totalPages = clampTotalPages(
      total,
      POSTGREST_PAGE_SIZE,
      'trainer-summary.getPrivateTrainerCount'
    )

    if (totalPages <= 1) {
      return countInRows(firstRows)
    }

    const restCounts = await fetchPagesInBatches(
      Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
      (page) => fetchPage(page, false).then(({ data }) => countInRows(data?.data ?? []))
    )

    return countInRows(firstRows) + restCounts.reduce((acc, value) => acc + value, 0)
  }

  return {
    async getSwimmingCoachOverviewCounts({
      to,
    }: GetSwimmingCoachOverviewCountsParams = {}): Promise<SwimmingCoachOverviewCounts> {
      const query = new URLSearchParams()
      query.append('count', 'true')
      query.append('data', 'false')
      query.append(
        'sum',
        [
          'total_swimming_coaches',
          'total_full_time_swimming_coaches',
          'total_freelance_swimming_coaches',
        ].join(',')
      )

      if (to) {
        query.append('filter', `first_swimming_assigned_at=lt.${to}`)
      }

      const { data } = await wrapApiCall(() =>
        client.get<{
          count?: string | number
          sum?: Record<string, string | number | null>
        }>(`/postgrest/dashboard_swimming_coach_profile_overview_view?${query.toString()}`)
      )

      return deserialize(
        {
          totalFullTimeSwimmingCoaches: toNumber(data?.sum?.total_full_time_swimming_coaches),
          totalSwimmingCoaches: toNumber(data?.sum?.total_swimming_coaches ?? data?.count),
          totalFreelanceSwimmingCoaches: toNumber(data?.sum?.total_freelance_swimming_coaches),
        },
        SwimmingCoachOverviewCounts
      )
    },
    async getTrainerPerformanceOverviewCounts({
      to,
    }: GetTrainerPerformanceOverviewCountsParams = {}): Promise<TrainerPerformanceOverviewCounts> {
      const trainerRows = await getTrainerListRows()
      const trainerIds = new Set(
        trainerRows.map((trainer) => toNumber(trainer.id)).filter((trainerId) => trainerId > 0)
      )
      const totalPrivateTrainers = await getPrivateTrainerCount({ trainerIds, to })

      return deserialize(
        {
          totalPrivateTrainers,
          totalFullTimePersonalTrainers: trainerRows.filter(
            (trainer) => trainer.contractType === 'PERMANENT'
          ).length,
          totalPersonalTrainers: trainerRows.length,
          totalFreelanceTrainers: trainerRows.filter(
            (trainer) => trainer.contractType === 'FREELANCE'
          ).length,
        },
        TrainerPerformanceOverviewCounts
      )
    },
    async getPtDistinctTrainerCount({
      trainerType,
      from,
      to,
    }: GetPtDistinctTrainerCountParams = {}): Promise<number> {
      const query = new URLSearchParams()
      query.append('select', 'trainer_id')
      query.append('distinct', 'trainer_id')
      query.append('count', 'true')
      query.append('data', 'false')
      if (from) {
        query.append('filter', `start_time=gte.${from}`)
      }

      if (to) {
        query.append('filter', `start_time=lt.${to}`)
      }

      if (trainerType) {
        query.append('filter', `trainer_type=eq.${trainerType}`)
      }

      const { data } = await wrapApiCall(() =>
        client.get<{ count?: string | number }>(
          `/postgrest/pt_booking_dashboard_view?${query.toString()}`
        )
      )

      return Number(data?.count ?? 0)
    },
    async getPtBookingDashboardRows({
      from,
      to,
    }: GetPtBookingDashboardRowsParams): Promise<PtBookingDashboardRow[]> {
      const fetchPage = (page: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append('select', PT_BOOKING_DASHBOARD_SELECT)
        query.append('page', String(page))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        if (withCount) query.append('count', 'true')
        query.append('order', 'start_time.asc')
        query.append('filter', `start_time=gte.${from}`)
        query.append('filter', `start_time=lt.${to}`)

        return wrapApiCall(() =>
          client.get<PtBookingDashboardListResponse>(
            `/postgrest/pt_booking_dashboard_view?${query.toString()}`
          )
        )
      }
      const mapRows = (data: PtBookingDashboardListResponse | undefined) =>
        (data?.data as Record<string, unknown>[] | undefined)?.map((item) =>
          deserialize(item, PtBookingDashboardRow)
        ) ?? []

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstData } = await fetchPage(1, true)
      const firstRows = mapRows(firstData)

      const total = Number(firstData?.pagination?.total ?? firstRows.length)
      const totalPages = clampTotalPages(
        total,
        POSTGREST_PAGE_SIZE,
        'trainer-summary.getPtBookingDashboardRows'
      )

      if (totalPages <= 1) {
        return firstRows
      }

      const restPages = await fetchPagesInBatches(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
        (page) => fetchPage(page, false).then(({ data }) => mapRows(data))
      )

      return [firstRows, ...restPages].flat()
    },
    async getSwimmingBookingDashboardRows({
      from,
      to,
    }: GetPtBookingDashboardRowsParams): Promise<PtBookingDashboardRow[]> {
      const fetchPage = (page: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append('select', PT_BOOKING_DASHBOARD_SELECT)
        query.append('page', String(page))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        if (withCount) query.append('count', 'true')
        query.append('order', 'start_time.asc')
        query.append('filter', `start_time=gte.${from}`)
        query.append('filter', `start_time=lt.${to}`)

        return wrapApiCall(() =>
          client.get<PtBookingDashboardListResponse>(
            `/postgrest/swimming_booking_dashboard_view?${query.toString()}`
          )
        )
      }
      const mapRows = (data: PtBookingDashboardListResponse | undefined) =>
        (data?.data as Record<string, unknown>[] | undefined)?.map((item) =>
          deserialize(item, PtBookingDashboardRow)
        ) ?? []

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstData } = await fetchPage(1, true)
      const firstRows = mapRows(firstData)

      const total = Number(firstData?.pagination?.total ?? firstRows.length)
      const totalPages = clampTotalPages(
        total,
        POSTGREST_PAGE_SIZE,
        'trainer-summary.getSwimmingBookingDashboardRows'
      )

      if (totalPages <= 1) {
        return firstRows
      }

      const restPages = await fetchPagesInBatches(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
        (page) => fetchPage(page, false).then(({ data }) => mapRows(data))
      )

      return [firstRows, ...restPages].flat()
    },
    async getPersonalTrainingGoalSummaryRows(
      from: string,
      to: string
    ): Promise<DashboardGoalSummaryRow[]> {
      const fetchPage = (page: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append('select', DASHBOARD_GOAL_SUMMARY_SELECT)
        query.append('page', String(page))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        if (withCount) query.append('count', 'true')
        query.append('order', 'member_full_name.asc')
        query.append('filter', 'training_type=eq.PERSONAL_TRAINER')
        query.append('filter', `start_date=lte.${to}`)
        // Temporarily disabled per request: apply conditional overlap handling in frontend.
        // query.append('filter', `end_date=gte.${from}`)

        return wrapApiCall(() =>
          client.get<DashboardGoalSummaryListResponse>(
            `/postgrest/dashboard_goal_summary_view?${query.toString()}`
          )
        )
      }
      const mapRows = (data: DashboardGoalSummaryListResponse | undefined) =>
        (data?.data as Record<string, unknown>[] | undefined)?.map((item) =>
          deserialize(item, DashboardGoalSummaryRow)
        ) ?? []

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstData } = await fetchPage(1, true)
      const firstRows = mapRows(firstData)

      const total = Number(firstData?.pagination?.total ?? firstRows.length)
      const totalPages = clampTotalPages(
        total,
        POSTGREST_PAGE_SIZE,
        'trainer-summary.getPersonalTrainingGoalSummaryRows'
      )

      if (totalPages <= 1) {
        return firstRows
      }

      const restPages = await fetchPagesInBatches(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
        (page) => fetchPage(page, false).then(({ data }) => mapRows(data))
      )

      return [firstRows, ...restPages].flat()
    },
    async getTrainingPlanSummaryRows({
      year,
      month,
    }: GetTrainingPlanSummaryRowsParams): Promise<TrainingPlanSummaryFactRow[]> {
      const fetchPage = (page: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append('select', TRAINING_PLAN_SUMMARY_FACT_SELECT)
        query.append('page', String(page))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        if (withCount) query.append('count', 'true')
        query.append('order', 'training_plan_created_at.asc,goal_session_group_id.asc')
        query.append('filter', `year_local=eq.${year}`)
        query.append('filter', `month_local=eq.${month}`)

        return wrapApiCall(() =>
          client.get<TrainingPlanSummaryFactListResponse>(
            `/postgrest/dashboard_training_plan_summary_fact_view?${query.toString()}`
          )
        )
      }
      const mapRows = (data: TrainingPlanSummaryFactListResponse | undefined) =>
        (data?.data as Record<string, unknown>[] | undefined)?.map((item) =>
          deserialize(item, TrainingPlanSummaryFactRow)
        ) ?? []

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstData } = await fetchPage(1, true)
      const firstRows = mapRows(firstData)

      const total = Number(firstData?.pagination?.total ?? firstRows.length)
      const totalPages = clampTotalPages(
        total,
        POSTGREST_PAGE_SIZE,
        'trainer-summary.getTrainingPlanSummaryRows'
      )

      if (totalPages <= 1) {
        return firstRows
      }

      const restPages = await fetchPagesInBatches(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
        (page) => fetchPage(page, false).then(({ data }) => mapRows(data))
      )

      return [firstRows, ...restPages].flat()
    },
    async getSwimmingGoalSummaryRows({
      to,
      memberType,
    }: GetSwimmingGoalSummaryRowsParams): Promise<DashboardGoalSummaryRow[]> {
      const fetchPage = (page: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append('select', DASHBOARD_GOAL_SUMMARY_SELECT)
        query.append('page', String(page))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        if (withCount) query.append('count', 'true')
        query.append('order', 'member_full_name.asc')
        query.append('filter', `member_type=eq.${memberType}`)
        query.append('filter', `start_date=lte.${to}`)
        // Temporarily disabled per request: apply conditional overlap handling in frontend.
        // query.append('filter', `end_date=gte.${from}`)

        return wrapApiCall(() =>
          client.get<DashboardGoalSummaryListResponse>(
            `/postgrest/dashboard_swimming_goal_summary_view?${query.toString()}`
          )
        )
      }
      const mapRows = (data: DashboardGoalSummaryListResponse | undefined) =>
        (data?.data as Record<string, unknown>[] | undefined)?.map((item) =>
          deserialize(item, DashboardGoalSummaryRow)
        ) ?? []

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstData } = await fetchPage(1, true)
      const firstRows = mapRows(firstData)

      const total = Number(firstData?.pagination?.total ?? firstRows.length)
      const totalPages = clampTotalPages(
        total,
        POSTGREST_PAGE_SIZE,
        'trainer-summary.getSwimmingGoalSummaryRows'
      )

      if (totalPages <= 1) {
        return firstRows
      }

      const restPages = await fetchPagesInBatches(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
        (page) => fetchPage(page, false).then(({ data }) => mapRows(data))
      )

      return [firstRows, ...restPages].flat()
    },
  }
}
